import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Text, Badge, Tooltip } from '@components/base/display';
import { Card, VStack, HStack, Grid, GridItem } from '@components/base/layout';
import { Breadcrumb } from '@components/base/navigation';
import { Alert, Spinner } from '@components/base/feedback';
import { tracesService } from '../../api/tracesService';
import type { Span, TraceDetail } from '../../api/tracesService';
import { buildSpanTree } from './spanTree';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseTraceTimestamp(s: string): Date {
  const t = s.replace(' ', 'T');
  const dot = t.indexOf('.');
  const trimmed = dot === -1 ? t : t.slice(0, dot + 4);
  return new Date(trimmed.endsWith('Z') ? trimmed : `${trimmed}Z`);
}

function formatTraceTimestamp(s: string): string {
  return parseTraceTimestamp(s).toLocaleString();
}

function formatDurationMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatDurationNs(ns: number): string {
  if (ns < 1_000_000) return `${ns}ns`;
  if (ns < 1_000_000_000) return `${(ns / 1_000_000).toFixed(1)}ms`;
  return `${(ns / 1_000_000_000).toFixed(2)}s`;
}

function spanKindShort(kind: string): string {
  switch (kind) {
    case 'SPAN_KIND_SERVER':
      return 'SRV';
    case 'SPAN_KIND_CLIENT':
      return 'CLI';
    case 'SPAN_KIND_INTERNAL':
      return 'INT';
    case 'SPAN_KIND_PRODUCER':
      return 'PRD';
    case 'SPAN_KIND_CONSUMER':
      return 'CON';
    default:
      return kind.replace('SPAN_KIND_', '').slice(0, 3) || '—';
  }
}

function spanStatusVariant(
  span: Span,
): 'success' | 'error' | 'default' | 'info' {
  if (span.status_code === 'STATUS_CODE_ERROR') return 'error';
  if (span.status_code === 'STATUS_CODE_OK') return 'success';
  return 'info';
}

function traceHeaderStatus(trace: TraceDetail): {
  variant: 'success' | 'error' | 'default';
  label: string;
} {
  const hasError = trace.spans.some(
    (s) => s.status_code === 'STATUS_CODE_ERROR',
  );
  if (hasError) return { variant: 'error', label: 'ERROR' };
  return { variant: 'success', label: 'OK' };
}

function mapTraceDetailError(error: unknown): { title: string; body: string } {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    if (status === 401) {
      return {
        title: 'Session expired',
        body: 'Your session has expired. Reload to sign in again.',
      };
    }
    if (status === 403) {
      return {
        title: 'Access denied',
        body: 'Your account does not have the `observability.view` permission. This permission is granted by a global RBAC role (e.g. `admin` or `viewer`) — tenant membership alone is not enough. Ask an administrator to assign you the `admin` or `viewer` role.',
      };
    }
    if (status === 404) {
      return { title: 'Trace not found', body: 'Trace not found.' };
    }
  }
  if (error instanceof Error) {
    return { title: 'Failed to load trace', body: error.message };
  }
  return {
    title: 'Failed to load trace',
    body: 'Trace not found or an unexpected error occurred.',
  };
}

// ---------------------------------------------------------------------------
// Helper: DetailField
// ---------------------------------------------------------------------------

function DetailField({
  label,
  children,
}: Readonly<{ label: string; children: React.ReactNode }>) {
  return (
    <VStack gap={1}>
      <Text size="sm" color="muted" weight="medium">
        {label}
      </Text>
      <div>{children}</div>
    </VStack>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function TraceDetailPage() {
  const { traceId } = useParams<{ traceId: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'observability', 'traces', traceId],
    queryFn: () => {
      if (!traceId) {
        return Promise.reject(new Error('Trace ID is required'));
      }
      return tracesService.get(traceId);
    },
    enabled: Boolean(traceId),
  });

  const trace = data?.data;

  const nodes = useMemo(
    () => (trace ? buildSpanTree(trace.spans) : []),
    [trace],
  );

  if (isLoading) {
    return (
      <VStack gap={6} align="center" className="py-12">
        <Spinner size="lg" />
        <Text color="muted">Loading trace...</Text>
      </VStack>
    );
  }

  if (error || !trace) {
    const mapped = error
      ? mapTraceDetailError(error)
      : {
          title: 'Failed to load trace',
          body: 'Trace not found or an unexpected error occurred.',
        };
    return (
      <VStack gap={6}>
        <Breadcrumb>
          <Breadcrumb.Item href="/admin">Admin</Breadcrumb.Item>
          <Breadcrumb.Item href="/admin/observability/traces">
            Traces
          </Breadcrumb.Item>
          <Breadcrumb.Item isCurrent>Details</Breadcrumb.Item>
        </Breadcrumb>
        <Alert variant="error" title={mapped.title}>
          {mapped.body}
        </Alert>
      </VStack>
    );
  }

  const shortTraceId = trace.trace_id.slice(0, 12);
  const headerStatus = traceHeaderStatus(trace);

  return (
    <VStack gap={6}>
      <Breadcrumb>
        <Breadcrumb.Item href="/admin">Admin</Breadcrumb.Item>
        <Breadcrumb.Item href="/admin/observability/traces">
          Traces
        </Breadcrumb.Item>
        <Breadcrumb.Item isCurrent>{shortTraceId}</Breadcrumb.Item>
      </Breadcrumb>

      <VStack gap={2}>
        <HStack gap={3} align="center">
          <Text as="h1" size="2xl" weight="bold">
            Trace {shortTraceId}
          </Text>
          <Badge variant={headerStatus.variant} size="sm">
            {headerStatus.label}
          </Badge>
        </HStack>
        <Text size="sm" color="muted">
          {trace.service_name} · {formatTraceTimestamp(trace.started_at)} ·{' '}
          {formatDurationMs(trace.duration_ms)}
        </Text>
      </VStack>

      <Card>
        <Card.Header title="Trace Details" />
        <Card.Content>
          <Grid columns={2} gap={4}>
            <GridItem>
              <DetailField label="Trace ID">
                <Text size="sm" className="font-mono break-all">
                  {trace.trace_id}
                </Text>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="Service">
                <Text size="sm">{trace.service_name}</Text>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="Root Span ID">
                <Text size="sm" className="font-mono break-all">
                  {trace.root_span_id ?? '—'}
                </Text>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="Started At">
                <Text size="sm">{formatTraceTimestamp(trace.started_at)}</Text>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="Duration">
                <Text size="sm">{formatDurationMs(trace.duration_ms)}</Text>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="Span Count">
                <Text size="sm">{trace.spans.length}</Text>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="User ID">
                <Text size="sm">{trace.user_id ?? '—'}</Text>
              </DetailField>
            </GridItem>
            <GridItem>
              <DetailField label="Tenant ID">
                <Text size="sm">{trace.tenant_id ?? '—'}</Text>
              </DetailField>
            </GridItem>
          </Grid>
        </Card.Content>
      </Card>

      <Card>
        <Card.Header title={`Spans (${trace.spans.length})`} />
        <Card.Content>
          <VStack gap={1}>
            {nodes.map(({ span, depth }) => (
              <div
                key={span.span_id}
                style={{ paddingLeft: depth * 16 }}
                data-testid="span-row"
                data-depth={depth}
              >
                <HStack
                  gap={3}
                  align="center"
                  className="py-2 border-b border-secondary-100"
                >
                  <Badge variant={spanStatusVariant(span)} size="sm">
                    {spanKindShort(span.kind)}
                  </Badge>
                  <Text weight="medium" className="font-mono text-sm">
                    {span.name}
                  </Text>
                  <Text size="sm" color="muted">
                    {formatDurationNs(span.duration_ns)}
                  </Text>
                  {span.attributes?.['http.method'] && (
                    <Text size="sm" color="muted">
                      {span.attributes['http.method']}
                    </Text>
                  )}
                  {span.attributes?.['db.statement'] && (
                    <Tooltip content={span.attributes['db.statement']}>
                      <Text size="sm" color="muted">
                        DB
                      </Text>
                    </Tooltip>
                  )}
                </HStack>
                {span.events && span.events.length > 0 && (
                  <VStack gap={0} className="pl-6 py-1">
                    {span.events.map((evt, i) => (
                      <Text
                        key={`${span.span_id}-${i}`}
                        size="sm"
                        color="muted"
                      >
                        · {evt.name} ({formatTraceTimestamp(evt.timestamp)})
                      </Text>
                    ))}
                  </VStack>
                )}
              </div>
            ))}
          </VStack>
        </Card.Content>
      </Card>
    </VStack>
  );
}
